/**
 * React hooks that wrap geoService with loading/error state.
 * Components just call useCountries(), useStates("IN"), useCities("IN","MH").
 */

import { useEffect, useState } from "react";
import { geoService, type Country, type State, type City, type GeoRegion } from "./service";

// ── useCountries ───────────────────────────────────────────────────────────

export function useCountries() {
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    geoService.getCountries()
      .then((data) => { if (!cancelled) { setCountries(data); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError("Failed to load countries"); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  return { countries, loading, error };
}

// ── useStates ──────────────────────────────────────────────────────────────

export function useStates(countryIso2: string) {
  const [states,  setStates]  = useState<State[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!countryIso2) { setStates([]); return; }
    let cancelled = false;
    setLoading(true);
    geoService.getStates(countryIso2)
      .then((data) => { if (!cancelled) { setStates(data); setLoading(false); } })
      .catch(() => { if (!cancelled) { setStates([]); setLoading(false); } });
    return () => { cancelled = true; };
  }, [countryIso2]);

  return { states, loading };
}

// ── useGeoRegions ──────────────────────────────────────────────────────────

export function useGeoRegions() {
  const [regions, setRegions] = useState<GeoRegion[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    geoService.getRegions()
      .then((data) => { if (!cancelled) { setRegions(data); setLoading(false); } })
      .catch(() => { if (!cancelled) { setRegions([]); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  return { regions, loading };
}

// ── useCities ──────────────────────────────────────────────────────────────

export function useCities(countryIso2: string, stateIso2?: string) {
  const [cities,  setCities]  = useState<City[]>([]);
  const [loading, setLoading] = useState(false);

  // fetch when country (and optional state) changes
  const key = `${countryIso2}|${stateIso2 ?? ""}`;

  useEffect(() => {
    if (!countryIso2) { setCities([]); return; }
    let cancelled = false;
    setLoading(true);
    geoService.getCities(countryIso2, stateIso2)
      .then((data) => { if (!cancelled) { setCities(data); setLoading(false); } })
      .catch(() => { if (!cancelled) { setCities([]); setLoading(false); } });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { cities, loading };
}
